import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Plus, 
    Search, 
    Filter, 
    Truck,
    Package,
    FileText,
    CheckCircle2,
    CheckCircle,
    ArrowRight,
    Building2,
    Clock,
    Beaker,
    Upload,
    Image,
    MapPin,
    DollarSign,
    Calendar,
    Route,
    RefreshCw,
    Loader2,
    ChevronDown,
    ChevronUp,
    Clipboard,
    History,
    Play,
    FileUp,
    Maximize2,
    Minimize2,
    TrendingUp,
    TrendingDown,
    Target,
    FolderOpen,
    Trash2,
    AlertTriangle,
    Edit3,
    UserX,
    Brain
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import LoadPriorityDashboard from '../components/LoadPriorityDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

// Format date
const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Get status badge style
const getStatusBadge = (status) => {
    const styles = {
        'available': { bg: 'bg-zinc-600', text: 'Available' },
        'booked_pending_confirmation': { bg: 'bg-blue-500', text: 'Staged' },
        'booked': { bg: 'bg-blue-600', text: 'Booked' },
        'dispatched': { bg: 'bg-purple-600', text: 'Dispatched' },
        'at_pickup': { bg: 'bg-orange-500', text: 'At Pickup' },
        'in_transit': { bg: 'bg-amber-600', text: 'In Transit' },
        'at_delivery': { bg: 'bg-teal-500', text: 'At Delivery' },
        'delivered': { bg: 'bg-green-600', text: 'Delivered' },
        'paperwork': { bg: 'bg-yellow-600', text: 'Paperwork' },
        'invoiced': { bg: 'bg-indigo-600', text: 'Invoiced' },
        'paid': { bg: 'bg-emerald-600', text: 'Paid' },
        'completed': { bg: 'bg-green-700', text: 'Completed' },
        'cancelled': { bg: 'bg-red-600', text: 'Cancelled' },
    };
    const style = styles[status] || styles['available'];
    return <Badge className={`${style.bg} text-white`}>{style.text}</Badge>;
};

// Helper to get truck location string
const getTruckLocation = (truck) => {
    // Priority: current_location > home_base
    if (truck.current_city && truck.current_state) {
        return `${truck.current_city}, ${truck.current_state}`;
    }
    if (truck.home_base_city && truck.home_base_state) {
        return `${truck.home_base_city}, ${truck.home_base_state}`;
    }
    return null;
};

// =====================================================
// FREIGHT HUB DENSITY SCORES (0-100) for Load Analysis
// =====================================================
const FREIGHT_HUB_SCORES = {
    'chicago': 98, 'los angeles': 97, 'dallas': 96, 'atlanta': 95,
    'houston': 94, 'memphis': 93, 'indianapolis': 92, 'columbus': 91,
    'louisville': 90, 'kansas city': 90, 'phoenix': 89, 'denver': 88,
    'nashville': 87, 'charlotte': 86, 'detroit': 85, 'st. louis': 85,
    'philadelphia': 84, 'cincinnati': 84, 'cleveland': 83, 'jacksonville': 82,
    'tampa': 81, 'orlando': 80, 'san antonio': 79, 'austin': 78,
    'san diego': 77, 'las vegas': 76, 'minneapolis': 75, 'omaha': 75,
    'oklahoma city': 74, 'raleigh': 73, 'baltimore': 72, 'pittsburgh': 71,
    'fresno': 70, 'sacramento': 70, 'salt lake city': 69, 'albuquerque': 68,
    'tucson': 67, 'baton rouge': 66, 'new orleans': 65, 'portland': 64,
    'san francisco': 63, 'boston': 62, 'new york': 61, 'miami': 60,
    'seattle': 55, 'richmond': 54, 'norfolk': 52
};

// Get hub score for a city
const getHubScore = (city) => {
    if (!city) return 50;
    return FREIGHT_HUB_SCORES[city.toLowerCase().trim()] || 50;
};

// Calculate market rate estimate
const calculateMarketRate = (load) => {
    const baseRpm = 2.50;
    const distanceFactor = load.distance < 300 ? 1.15 : load.distance < 600 ? 1.05 : 1.0;
    const estimatedMarketRpm = baseRpm * distanceFactor;
    const offeredRpm = load.distance > 0 ? (load.rate || load.gross_pay) / load.distance : 0;
    const difference = offeredRpm - estimatedMarketRpm;
    const negotiationTarget = estimatedMarketRpm * 1.05;
    
    return {
        marketRpm: estimatedMarketRpm,
        offeredRpm: offeredRpm,
        difference: difference,
        negotiationTarget: negotiationTarget,
        isAboveMarket: difference >= 0
    };
};

// Calculate load score
const calculateLoadScore = (load, truck = null) => {
    let score = 0;
    const breakdown = {};
    
    // 1. RPM SCORE (max 25)
    const rpm = load.distance > 0 ? (load.rate || load.gross_pay) / load.distance : 0;
    if (rpm >= 2.50) breakdown.rpm = 25;
    else if (rpm >= 2.20) breakdown.rpm = 20;
    else if (rpm >= 2.00) breakdown.rpm = 15;
    else if (rpm >= 1.80) breakdown.rpm = 10;
    else breakdown.rpm = 5;
    score += breakdown.rpm;
    
    // 2. HUB SCORE (max 20)
    const hubScore = getHubScore(load.destination_city);
    if (hubScore >= 90) breakdown.hub = 20;
    else if (hubScore >= 80) breakdown.hub = 15;
    else if (hubScore >= 70) breakdown.hub = 10;
    else breakdown.hub = 3;
    score += breakdown.hub;
    
    // 3. DEADHEAD SCORE (max 15) - estimate if not provided
    const deadheadMiles = load.deadhead_miles || 999;
    if (deadheadMiles <= 50) breakdown.deadhead = 15;
    else if (deadheadMiles <= 100) breakdown.deadhead = 10;
    else if (deadheadMiles <= 200) breakdown.deadhead = 5;
    else breakdown.deadhead = 0;
    score += breakdown.deadhead;
    
    // 4. REVENUE GOAL (max 15)
    const rate = load.rate || load.gross_pay || 0;
    if (rate >= 2000) breakdown.revenue = 15;
    else if (rate >= 1500) breakdown.revenue = 10;
    else if (rate >= 1200) breakdown.revenue = 5;
    else breakdown.revenue = 0;
    score += breakdown.revenue;
    
    // 5. RISK PENALTIES
    breakdown.riskPenalty = 0;
    if ((load.broker_credit_score || 100) < 90) breakdown.riskPenalty -= 20;
    if ((load.broker_days_to_pay || 0) > 30) breakdown.riskPenalty -= 10;
    score += breakdown.riskPenalty;
    
    return { totalScore: Math.max(0, score), breakdown, hubScore };
};

// ==================== PANEL 1: LOAD SIMULATION ====================
function LoadSimulationPanel({ trucks, onLoadGenerated }) {
    const [selectedTruck, setSelectedTruck] = useState('');
    const [loadCount, setLoadCount] = useState(5);
    const [generating, setGenerating] = useState(false);

    const handleGenerateLoads = async () => {
        if (!selectedTruck) {
            toast.error('Please select a truck');
            return;
        }

        setGenerating(true);
        try {
            const truck = trucks.find(t => t.truck_id === selectedTruck);
            
            if (!selectedTruck) {
                toast.error('Please select a truck first');
                return;
            }

            console.log('[GenerateLoads] Generating for truck:', selectedTruck, 'count:', loadCount);

            // Use NEW truck-aware endpoint for realistic loads
            const res = await fetch(`${API_URL}/api/simulation/generate-loads-for-truck/${selectedTruck}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    count: loadCount,
                    clear_existing: false  // Don't clear other trucks' loads
                })
            });

            console.log('[GenerateLoads] Response status:', res.status);
            
            // Read response once
            const responseText = await res.text();
            console.log('[GenerateLoads] Response length:', responseText.length);
            
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (parseErr) {
                console.error('[GenerateLoads] JSON parse error:', parseErr);
                toast.error('Failed to generate loads', { description: responseText || 'Invalid response' });
                return;
            }

            if (res.ok) {
                const stats = data.generation_stats || {};
                const location = data.truck?.location || getTruckLocation(truck) || 'truck location';
                
                toast.success(`Generated ${data.loads_created || loadCount} loads near ${location}`, {
                    description: `${stats.good_loads || 0} nearby, ${stats.edge_loads || 0} edge, ${stats.out_of_range_loads || 0} test loads`
                });
                // Refresh the loads list to show newly created loads
                onLoadGenerated?.();
            } else {
                const errorMsg = data.detail || data.message || data.error || 'Failed to generate loads';
                console.error('[GenerateLoads] Error:', errorMsg);
                toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
            }
        } catch (e) {
            console.error('[GenerateLoads] Exception:', e);
            toast.error('Failed to generate loads', { description: e.message });
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <Beaker className="w-4 h-4 text-purple-400" />
                    Load Simulation (Testing)
                </CardTitle>
                <p className="text-xs text-zinc-500 mt-1">
                    Generate test loads based on truck location for Command Center testing
                </p>
            </CardHeader>
            <CardContent className="space-y-4">
                {trucks.length === 0 ? (
                    <div className="text-center py-6 text-zinc-500">
                        <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No trucks available.</p>
                        <p className="text-xs mt-1">Add trucks in the Trucks page first.</p>
                    </div>
                ) : (
                <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Truck Selection */}
                    <div className="space-y-2">
                        <Label className="text-zinc-400 text-xs">Select Truck</Label>
                        <Select value={selectedTruck} onValueChange={setSelectedTruck}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="truck-select-trigger">
                                <SelectValue placeholder="Choose truck..." />
                            </SelectTrigger>
                            <SelectContent>
                                {trucks.map(truck => {
                                    const location = getTruckLocation(truck);
                                    return (
                                        <SelectItem key={truck.truck_id} value={truck.truck_id} data-testid={`truck-option-${truck.truck_id}`}>
                                            <div className="flex items-center gap-2">
                                                <Truck className="w-3 h-3" />
                                                <span>{truck.name || truck.truck_id}</span>
                                                {location && (
                                                    <span className="text-zinc-500 text-xs">
                                                        — {location}
                                                    </span>
                                                )}
                                            </div>
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Load Count */}
                    <div className="space-y-2">
                        <Label className="text-zinc-400 text-xs">Number of Loads</Label>
                        <Select value={String(loadCount)} onValueChange={(v) => setLoadCount(Number(v))}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {[3, 5, 10, 15, 20].map(n => (
                                    <SelectItem key={n} value={String(n)}>{n} loads</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Generate Button */}
                    <div className="flex items-end">
                        <Button
                            onClick={handleGenerateLoads}
                            disabled={generating || !selectedTruck}
                            className="w-full bg-purple-600 hover:bg-purple-700"
                            data-testid="generate-loads-btn"
                        >
                            {generating ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Play className="w-4 h-4 mr-2" />
                            )}
                            Generate Test Loads
                        </Button>
                    </div>
                </div>
                </>
                )}
            </CardContent>
        </Card>
    );
}

// ==================== EXPANDED LOAD DETAILS COMPONENT ====================
// Lazy-loaded when user expands a load card for performance
function LoadExpandedDetails({ load, getTruckName, assignedTruck }) {
    const [docStatus, setDocStatus] = useState({ has_rate_con: false, has_bol: false, has_pod: false, loading: true });
    
    // Fetch document status when component mounts
    useEffect(() => {
        const fetchDocStatus = async () => {
            try {
                const res = await fetch(`${API_URL}/api/loads/${load.load_id}/documents/check`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    setDocStatus({ ...data, loading: false });
                } else {
                    setDocStatus({ has_rate_con: false, has_bol: false, has_pod: false, loading: false });
                }
            } catch (e) {
                console.error('Failed to fetch doc status:', e);
                setDocStatus({ has_rate_con: false, has_bol: false, has_pod: false, loading: false });
            }
        };
        fetchDocStatus();
    }, [load.load_id]);
    
    // Calculate analysis data only when expanded
    const marketData = calculateMarketRate(load);
    const scoreData = calculateLoadScore(load);
    const hubScore = getHubScore(load.destination_city);
    
    return (
        <div className="px-3 pb-3 border-t border-zinc-700 bg-zinc-900/30">
            {/* Basic Info Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs py-3">
                <div>
                    <span className="text-zinc-500 block">Broker</span>
                    <p className="text-zinc-300 font-medium">{load.broker_name || '-'}</p>
                    {/* Broker Credit Intelligence */}
                    <div className="flex items-center gap-3 mt-1">
                        <span className={`text-xs ${
                            (load.broker_credit_score || 0) >= 90 ? 'text-green-400' : 
                            (load.broker_credit_score || 0) >= 75 ? 'text-amber-400' : 'text-red-400'
                        }`}>
                            Credit: {load.broker_credit_score || 'N/A'}
                        </span>
                        <span className={`text-xs ${
                            (load.broker_days_to_pay || 0) <= 21 ? 'text-green-400' : 
                            (load.broker_days_to_pay || 0) <= 30 ? 'text-amber-400' : 'text-red-400'
                        }`}>
                            {load.broker_days_to_pay ? `${load.broker_days_to_pay}d pay` : ''}
                        </span>
                    </div>
                </div>
                <div>
                    <span className="text-zinc-500 block">Pickup</span>
                    <p className="text-zinc-300">{formatDate(load.pickup_date)}</p>
                </div>
                <div>
                    <span className="text-zinc-500 block">Deadhead</span>
                    <p className="text-zinc-300">{load.deadhead_miles ? `${load.deadhead_miles} mi` : 'N/A'}</p>
                </div>
                <div>
                    <span className="text-zinc-500 block">Assigned Truck</span>
                    <p className="text-zinc-300">{assignedTruck ? getTruckName(assignedTruck) : 'None'}</p>
                </div>
            </div>
            
            {/* Market Analysis Section */}
            <div className="border-t border-zinc-700/50 pt-3 mt-1">
                <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-3.5 h-3.5 text-cyan-400" />
                    <span className="text-cyan-400 text-xs font-semibold uppercase">Market Analysis</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div>
                        <span className="text-zinc-500 block">Market Rate</span>
                        <p className="text-white font-mono">${marketData.marketRpm.toFixed(2)}/mi</p>
                    </div>
                    <div>
                        <span className="text-zinc-500 block">Difference</span>
                        <p className={`font-mono flex items-center gap-1 ${marketData.isAboveMarket ? 'text-green-400' : 'text-red-400'}`}>
                            {marketData.isAboveMarket ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {marketData.difference >= 0 ? '+' : ''}{marketData.difference.toFixed(2)}/mi
                        </p>
                    </div>
                    <div>
                        <span className="text-zinc-500 block">Target Rate</span>
                        <p className="text-amber-400 font-mono font-semibold">${marketData.negotiationTarget.toFixed(2)}/mi</p>
                    </div>
                    <div>
                        <span className="text-zinc-500 block">Hub Score</span>
                        <div className="flex items-center gap-1">
                            <span className={`font-semibold ${hubScore >= 80 ? 'text-green-400' : hubScore >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                                {hubScore}
                            </span>
                            <span className="text-zinc-500">/100</span>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Load Score Section */}
            <div className="border-t border-zinc-700/50 pt-3 mt-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Target className="w-3.5 h-3.5 text-orange-400" />
                        <span className="text-orange-400 text-xs font-semibold uppercase">Load Score</span>
                    </div>
                    <Badge className={`text-sm font-bold px-2 ${
                        scoreData.totalScore >= 70 ? 'bg-green-600/50 text-green-300' :
                        scoreData.totalScore >= 50 ? 'bg-amber-600/50 text-amber-300' :
                        scoreData.totalScore >= 30 ? 'bg-orange-600/50 text-orange-300' :
                        'bg-red-600/50 text-red-300'
                    }`}>
                        {scoreData.totalScore} pts
                    </Badge>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-xs">
                    <div className="bg-zinc-800/50 rounded p-1.5 text-center">
                        <span className="text-zinc-500 block text-[10px]">RPM</span>
                        <span className="text-white font-semibold">{scoreData.breakdown.rpm}</span>
                    </div>
                    <div className="bg-zinc-800/50 rounded p-1.5 text-center">
                        <span className="text-zinc-500 block text-[10px]">Hub</span>
                        <span className="text-white font-semibold">{scoreData.breakdown.hub}</span>
                    </div>
                    <div className="bg-zinc-800/50 rounded p-1.5 text-center">
                        <span className="text-zinc-500 block text-[10px]">Deadhead</span>
                        <span className="text-white font-semibold">{scoreData.breakdown.deadhead}</span>
                    </div>
                    <div className="bg-zinc-800/50 rounded p-1.5 text-center">
                        <span className="text-zinc-500 block text-[10px]">Revenue</span>
                        <span className="text-white font-semibold">{scoreData.breakdown.revenue}</span>
                    </div>
                    <div className="bg-zinc-800/50 rounded p-1.5 text-center col-span-2 sm:col-span-1">
                        <span className="text-zinc-500 block text-[10px]">Risk</span>
                        <span className={`font-semibold ${scoreData.breakdown.riskPenalty < 0 ? 'text-red-400' : 'text-white'}`}>
                            {scoreData.breakdown.riskPenalty}
                        </span>
                    </div>
                </div>
            </div>
            
            {/* Documents Section */}
            <div className="border-t border-zinc-700/50 pt-3 mt-3">
                <p className="text-xs text-zinc-500 mb-2">Documents</p>
                <div className="flex flex-wrap gap-2">
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className={`text-xs border-zinc-700 h-7 ${docStatus.has_rate_con ? 'border-green-600 text-green-400' : 'opacity-50'}`}
                        data-testid={`doc-ratecon-${load.load_id}`}
                    >
                        <FileText className="w-3 h-3 mr-1" />
                        Rate Con {docStatus.has_rate_con && <CheckCircle className="w-3 h-3 ml-1" />}
                    </Button>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className={`text-xs border-zinc-700 h-7 ${docStatus.has_bol ? 'border-green-600 text-green-400' : 'opacity-50'}`}
                        data-testid={`doc-bol-${load.load_id}`}
                    >
                        <FileText className="w-3 h-3 mr-1" />
                        BOL {docStatus.has_bol && <CheckCircle className="w-3 h-3 ml-1" />}
                    </Button>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className={`text-xs border-zinc-700 h-7 ${docStatus.has_pod ? 'border-green-600 text-green-400' : 'opacity-50'}`}
                        data-testid={`doc-pod-${load.load_id}`}
                    >
                        <FileText className="w-3 h-3 mr-1" />
                        POD {docStatus.has_pod && <CheckCircle className="w-3 h-3 ml-1" />}
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ==================== PANEL 2: ALL LOADS ====================
function AllLoadsPanel({ loads, trucks, onRefresh, onStageLoad, onAdvanceLoad, onUnassignLoad, isExpanded, onToggleExpand, onOpenLoad }) {
    const [filter, setFilter] = useState('all');
    const [truckFilter, setTruckFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedLoad, setExpandedLoad] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [stageDialog, setStageDialog] = useState({ open: false, loadId: null });
    const [selectedTruckForStage, setSelectedTruckForStage] = useState('');
    
    // Unassign modal state
    const [unassignDialog, setUnassignDialog] = useState({ open: false, loadId: null, loadInfo: null });
    const [unassignReason, setUnassignReason] = useState('');
    const [unassignLoading, setUnassignLoading] = useState(false);
    
    // Unassign reasons
    const unassignReasons = [
        'Broker confirmation failed',
        'Switching to different truck',
        'Dispatch error correction',
        'Driver unavailable',
        'Equipment issue',
        'Rate renegotiation required',
        'Load details changed',
        'Other'
    ];
    // Helper to get truck name from truck_id
    const getTruckName = (truckId) => {
        if (!truckId) return '-';
        const truck = trucks.find(t => t.truck_id === truckId);
        return truck?.name || truckId; // Fallback to ID if not found
    };

    // All load statuses
    const allStatuses = ['available', 'booked_pending_confirmation', 'booked', 'dispatched', 'at_pickup', 'in_transit', 'at_delivery', 'delivered', 'paperwork', 'invoiced', 'paid', 'completed'];
    
    // Active = still in workflow (booking → delivery → billing process)
    // DELIVERED stays Active because paperwork/billing may be pending
    const activeStatuses = ['booked_pending_confirmation', 'booked', 'dispatched', 'at_pickup', 'in_transit', 'at_delivery', 'delivered', 'paperwork', 'invoiced', 'paid'];
    
    // Completed = fully finished (all paperwork and billing done)
    const completedStatuses = ['completed'];
    
    const filteredLoads = loads.filter(load => {
        // Include all valid statuses
        if (!allStatuses.includes(load.status)) return false;
        
        // Apply status filter
        if (filter === 'available' && load.status !== 'available') return false;
        if (filter === 'active' && !activeStatuses.includes(load.status)) return false;
        if (filter === 'completed' && !completedStatuses.includes(load.status)) return false;
        
        // Apply truck filter
        if (truckFilter !== 'all') {
            const loadTruckId = load.truck_id || load.assigned_truck_id || load.suggested_truck_id;
            if (loadTruckId !== truckFilter) return false;
        }
        
        // Apply search
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return (
                load.load_id?.toLowerCase().includes(q) ||
                load.origin_city?.toLowerCase().includes(q) ||
                load.destination_city?.toLowerCase().includes(q) ||
                load.broker_name?.toLowerCase().includes(q)
            );
        }
        
        return true;
    });

    const openStageDialog = (e, loadId) => {
        e.stopPropagation();
        setSelectedTruckForStage('');
        setStageDialog({ open: true, loadId });
    };

    const handleConfirmStage = async () => {
        if (!selectedTruckForStage) {
            toast.error('Please select a truck');
            return;
        }
        
        setActionLoading(stageDialog.loadId);
        setStageDialog({ open: false, loadId: null });
        
        try {
            await onStageLoad(stageDialog.loadId, selectedTruckForStage);
        } finally {
            setActionLoading(null);
            setSelectedTruckForStage('');
        }
    };

    const handleAdvance = async (e, loadId) => {
        e.stopPropagation();
        setActionLoading(loadId);
        try {
            await onAdvanceLoad(loadId);
        } finally {
            setActionLoading(null);
        }
    };

    // Open unassign dialog
    const openUnassignDialog = (e, load) => {
        e.stopPropagation();
        setUnassignReason('');
        setUnassignDialog({ 
            open: true, 
            loadId: load.load_id, 
            loadInfo: `${load.origin_city}, ${load.origin_state} → ${load.destination_city}, ${load.destination_state}`
        });
    };

    // Handle unassign confirmation
    const handleConfirmUnassign = async () => {
        if (!unassignReason) {
            toast.error('Please select a reason for unassignment');
            return;
        }
        
        setUnassignLoading(true);
        try {
            await onUnassignLoad(unassignDialog.loadId, unassignReason);
            setUnassignDialog({ open: false, loadId: null, loadInfo: null });
            setUnassignReason('');
        } finally {
            setUnassignLoading(false);
        }
    };

    // Get action buttons based on status
    const getActionButtons = (load) => {
        const isLoading = actionLoading === load.load_id;
        const unassignableStatuses = ['booked_pending_confirmation', 'booked', 'dispatched'];
        const canUnassign = unassignableStatuses.includes(load.status) && (load.truck_id || load.assigned_truck_id);
        
        if (load.status === 'available') {
            return (
                <Button
                    size="sm"
                    onClick={(e) => openStageDialog(e, load.load_id)}
                    disabled={isLoading}
                    className="bg-amber-600 hover:bg-amber-700 h-7 text-xs"
                    data-testid={`stage-btn-${load.load_id}`}
                >
                    {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3 mr-1" />}
                    Stage
                </Button>
            );
        }
        
        if (activeStatuses.includes(load.status)) {
            return (
                <div className="flex items-center gap-1">
                    {canUnassign && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => openUnassignDialog(e, load)}
                            disabled={isLoading}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-7 text-xs"
                            data-testid={`unassign-btn-${load.load_id}`}
                            title="Unassign from truck"
                        >
                            <UserX className="w-3 h-3" />
                        </Button>
                    )}
                    <Button
                        size="sm"
                        onClick={(e) => handleAdvance(e, load.load_id)}
                        disabled={isLoading}
                        className="bg-blue-600 hover:bg-blue-700 h-7 text-xs"
                        data-testid={`advance-btn-${load.load_id}`}
                    >
                        {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3 mr-1" />}
                        Advance
                    </Button>
                </div>
            );
        }
        
        return null; // Completed loads have no action
    };

    // Open button - opens the Load Record Document Center
    const handleOpenLoad = (e, loadId) => {
        e.stopPropagation();
        onOpenLoad(loadId);
    };

    return (
        <Card className={`bg-zinc-900/50 border-zinc-800 ${isExpanded ? 'fixed inset-2 sm:inset-4 z-50 flex flex-col overflow-hidden w-auto max-w-full' : ''}`}>
            <CardHeader className="pb-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Package className="w-4 h-4 text-blue-400" />
                        All Loads
                        {isExpanded && (
                            <Badge className="bg-blue-600/50 text-blue-300 text-[10px] ml-2">
                                Expanded View
                            </Badge>
                        )}
                    </CardTitle>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onRefresh}
                            className="text-zinc-400"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onToggleExpand}
                            className="text-zinc-400 hover:text-white"
                            data-testid="expand-loads-btn"
                        >
                            {isExpanded ? (
                                <Minimize2 className="w-4 h-4" />
                            ) : (
                                <Maximize2 className="w-4 h-4" />
                            )}
                        </Button>
                    </div>
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                    {isExpanded ? 'Full screen view • Click minimize to return' : 'Available, active, and completed loads'}
                </p>
            </CardHeader>
            <CardContent className={`space-y-4 ${isExpanded ? 'flex-1 overflow-hidden flex flex-col' : ''}`}>
                {/* Filters Row */}
                <div className="flex flex-wrap gap-2 items-center flex-shrink-0">
                    {/* Status Filters */}
                    <div className="flex gap-1 flex-wrap">
                        {['all', 'priority', 'available', 'active', 'completed'].map(f => (
                            <Button
                                key={f}
                                variant={filter === f ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setFilter(f)}
                                className={`text-xs sm:text-sm ${
                                    filter === f 
                                        ? (f === 'priority' ? 'bg-purple-600' : 'bg-blue-600') 
                                        : 'border-zinc-700'
                                }`}
                                data-testid={`filter-${f}-btn`}
                            >
                                {f === 'priority' && <Brain className="w-3 h-3 mr-1" />}
                                {f.charAt(0).toUpperCase() + f.slice(1)}
                            </Button>
                        ))}
                    </div>
                    
                    {/* Truck Filter - hide when on priority view */}
                    {filter !== 'priority' && (
                        <Select value={truckFilter} onValueChange={setTruckFilter}>
                            <SelectTrigger className="w-[140px] sm:w-[160px] bg-zinc-800 border-zinc-700 h-9" data-testid="truck-filter-dropdown">
                                <Truck className="w-3 h-3 mr-1 text-zinc-400" />
                                <SelectValue placeholder="All Trucks" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Trucks</SelectItem>
                                {trucks.map(truck => (
                                    <SelectItem key={truck.truck_id} value={truck.truck_id}>
                                        {truck.name || truck.truck_id}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    
                    {/* Search - hide when on priority view */}
                    {filter !== 'priority' && (
                        <div className="relative flex-1 min-w-[140px] sm:min-w-[180px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                            <Input
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 bg-zinc-800 border-zinc-700"
                            />
                        </div>
                    )}
                </div>

                {/* Priority Dashboard - Show when priority filter is selected */}
                {filter === 'priority' ? (
                    <LoadPriorityDashboard onStageLoad={onStageLoad} />
                ) : (
                    /* Loads List */
                    <div className={`space-y-1.5 overflow-y-auto overflow-x-hidden ${isExpanded ? 'flex-1' : 'max-h-[500px]'}`}>
                    {filteredLoads.length === 0 ? (
                        <div className="text-center py-8 text-zinc-500">
                            <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>No loads found</p>
                        </div>
                    ) : (
                        filteredLoads.map(load => {
                            const ratePerMile = load.distance > 0 ? ((load.rate || load.gross_pay) / load.distance).toFixed(2) : '0.00';
                            const assignedTruck = load.truck_id || load.assigned_truck_id || load.suggested_truck_id;
                            const isExpansionOpen = expandedLoad === load.load_id;
                            
                            return (
                                <div
                                    key={load.load_id}
                                    className="bg-zinc-800/50 rounded-lg border border-zinc-700 overflow-hidden"
                                    data-testid={`load-item-${load.load_id}`}
                                >
                                    {/* COLLAPSED VIEW - Lightweight for fast scrolling */}
                                    <div
                                        className="p-2.5 cursor-pointer hover:bg-zinc-700/30 transition-colors"
                                        onClick={() => setExpandedLoad(isExpansionOpen ? null : load.load_id)}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            {/* Left: Route Info */}
                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <div className="min-w-0 flex-1">
                                                    {/* Load ID */}
                                                    <p className="text-xs text-zinc-500 font-mono mb-0.5" data-testid={`load-id-${load.load_id}`}>
                                                        {load.load_id}
                                                    </p>
                                                    <p className="text-sm text-white font-medium truncate">
                                                        {load.origin_city} → {load.destination_city}
                                                    </p>
                                                    <div className="flex items-center gap-2 text-xs text-zinc-400 mt-0.5">
                                                        <span className="text-green-400 font-semibold">{formatCurrency(load.rate || load.gross_pay)}</span>
                                                        <span>•</span>
                                                        <span>{load.distance || 0} mi</span>
                                                        <span>•</span>
                                                        <span className="text-cyan-400">${ratePerMile}/mi</span>
                                                        {/* Broker Credit Score - Risk at a glance */}
                                                        {load.broker_credit_score && (
                                                            <>
                                                                <span>•</span>
                                                                <span className={`${
                                                                    load.broker_credit_score >= 90 ? 'text-green-400' : 
                                                                    load.broker_credit_score >= 75 ? 'text-amber-400' : 'text-red-400'
                                                                }`}>
                                                                    Cr:{load.broker_credit_score}
                                                                </span>
                                                            </>
                                                        )}
                                                        {assignedTruck && (
                                                            <>
                                                                <span>•</span>
                                                                <span className="text-amber-400 flex items-center gap-0.5">
                                                                    <Truck className="w-3 h-3" />
                                                                    {getTruckName(assignedTruck)}
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Right: Status + Actions */}
                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                                {load.is_simulated && (
                                                    <Badge className="bg-purple-600/50 text-purple-300 text-[9px] px-1 py-0">SIM</Badge>
                                                )}
                                                {load.source === 'import' && (
                                                    <Badge className="bg-cyan-600/50 text-cyan-300 text-[9px] px-1 py-0">IMP</Badge>
                                                )}
                                                {getStatusBadge(load.status)}
                                                {/* Edit in Office Desk button */}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.location.href = `/office-desk/${load.load_id}`;
                                                    }}
                                                    className="border-orange-700 text-orange-400 hover:bg-orange-900/30 h-7 text-xs"
                                                    data-testid={`edit-btn-${load.load_id}`}
                                                    title="Edit in Office Desk"
                                                >
                                                    <Edit3 className="w-3 h-3" />
                                                </Button>
                                                {/* Open Load Record button */}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={(e) => handleOpenLoad(e, load.load_id)}
                                                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-700 h-7 text-xs"
                                                    data-testid={`open-btn-${load.load_id}`}
                                                >
                                                    <FolderOpen className="w-3 h-3 mr-1" />
                                                    Open
                                                </Button>
                                                {getActionButtons(load)}
                                                {isExpansionOpen ? (
                                                    <ChevronUp className="w-4 h-4 text-zinc-500" />
                                                ) : (
                                                    <ChevronDown className="w-4 h-4 text-zinc-500" />
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* EXPANDED VIEW - Detailed Analysis (only renders when opened) */}
                                    {isExpansionOpen && (
                                        <LoadExpandedDetails 
                                            load={load} 
                                            getTruckName={getTruckName}
                                            assignedTruck={assignedTruck}
                                        />
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
                )}

                {/* Summary - hide when on priority view */}
                {filter !== 'priority' && (
                    <div className="flex justify-between text-xs text-zinc-500 pt-2 border-t border-zinc-700">
                        <span>{filteredLoads.length} loads</span>
                        <span>
                            Total Revenue: {formatCurrency(filteredLoads.reduce((sum, l) => sum + (l.rate || l.gross_pay || 0), 0))}
                        </span>
                    </div>
                )}
            </CardContent>

            {/* Stage Dialog - Select Truck */}
            <Dialog open={stageDialog.open} onOpenChange={(open) => setStageDialog({ open, loadId: open ? stageDialog.loadId : null })}>
                <DialogContent className="bg-zinc-900 border-zinc-700">
                    <DialogHeader>
                        <DialogTitle>Stage Load for Booking</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Select a truck to assign this load to. The load will be staged for the Operations workflow.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label className="text-zinc-400 text-sm mb-2 block">Select Truck</Label>
                        <Select value={selectedTruckForStage} onValueChange={setSelectedTruckForStage}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                <SelectValue placeholder="Choose a truck..." />
                            </SelectTrigger>
                            <SelectContent>
                                {trucks.map(truck => (
                                    <SelectItem key={truck.truck_id} value={truck.truck_id}>
                                        <div className="flex items-center gap-2">
                                            <Truck className="w-3 h-3" />
                                            <span>{truck.name || truck.truck_id}</span>
                                            {truck.home_base_city && (
                                                <span className="text-zinc-500 text-xs">
                                                    — {truck.home_base_city}, {truck.home_base_state}
                                                </span>
                                            )}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setStageDialog({ open: false, loadId: null })} className="border-zinc-700">
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleConfirmStage} 
                            disabled={!selectedTruckForStage}
                            className="bg-amber-600 hover:bg-amber-700"
                        >
                            <Clock className="w-4 h-4 mr-2" />
                            Stage Load
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Unassign Dialog - Reason Required */}
            <Dialog open={unassignDialog.open} onOpenChange={(open) => {
                if (!open) {
                    setUnassignDialog({ open: false, loadId: null, loadInfo: null });
                    setUnassignReason('');
                }
            }}>
                <DialogContent className="bg-zinc-900 border-zinc-700">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-400">
                            <UserX className="w-5 h-5" />
                            Unassign Load from Truck
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            This will remove the truck assignment and return the load to "Available" status.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {unassignDialog.loadInfo && (
                        <div className="bg-zinc-800/50 p-3 rounded-lg border border-zinc-700">
                            <p className="text-sm text-zinc-400">Load:</p>
                            <p className="text-white font-medium">{unassignDialog.loadInfo}</p>
                        </div>
                    )}
                    
                    <div className="py-2">
                        <Label className="text-zinc-400 text-sm mb-2 block">
                            Reason for Unassignment <span className="text-red-400">*</span>
                        </Label>
                        <Select value={unassignReason} onValueChange={setUnassignReason}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                <SelectValue placeholder="Select a reason..." />
                            </SelectTrigger>
                            <SelectContent>
                                {unassignReasons.map(reason => (
                                    <SelectItem key={reason} value={reason}>
                                        {reason}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-400">
                        <AlertTriangle className="w-4 h-4 inline mr-2" />
                        This action will be logged in the audit trail.
                    </div>
                    
                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setUnassignDialog({ open: false, loadId: null, loadInfo: null });
                                setUnassignReason('');
                            }} 
                            className="border-zinc-700"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleConfirmUnassign} 
                            disabled={!unassignReason || unassignLoading}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-unassign-btn"
                        >
                            {unassignLoading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <UserX className="w-4 h-4 mr-2" />
                            )}
                            Unassign Load
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

// ==================== PANEL 3: IMPORT LOAD ENTRY ====================
function ImportLoadEntryPanel({ onLoadCreated }) {
    const [mode, setMode] = useState('paste'); // 'paste' or 'screenshot'
    const [pastedText, setPastedText] = useState('');
    const [screenshotFile, setScreenshotFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [lastImported, setLastImported] = useState(null);

    // Parse and auto-insert load from pasted text
    const handleParsePaste = async () => {
        if (!pastedText.trim()) {
            toast.error('Please paste load information first');
            return;
        }

        setProcessing(true);
        try {
            // Parse the pasted text
            const parsed = parseLoadText(pastedText);
            
            if (!parsed.origin_city || !parsed.destination_city) {
                toast.error('Could not extract origin and destination. Please check the pasted text.');
                setProcessing(false);
                return;
            }

            // Auto-insert the load
            const loadData = {
                origin_city: parsed.origin_city,
                origin_state: parsed.origin_state || '',
                destination_city: parsed.destination_city,
                destination_state: parsed.destination_state || '',
                rate: parsed.rate || 0,
                gross_pay: parsed.rate || 0,
                distance: parsed.distance || 0,
                broker_name: parsed.broker_name || 'Imported Load',
                pickup_date: parsed.pickup_date || new Date().toISOString().split('T')[0],
                equipment_type: parsed.equipment_type || 'Van',
                source: 'import',
                status: 'available',
                is_simulated: false
            };

            const res = await fetch(`${API_URL}/api/loads/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(loadData)
            });

            if (res.ok) {
                const data = await res.json();
                toast.success(`Load ${data.load_id} imported successfully!`);
                setLastImported({
                    load_id: data.load_id,
                    route: `${parsed.origin_city} → ${parsed.destination_city}`,
                    rate: parsed.rate
                });
                setPastedText('');
                onLoadCreated?.();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to import load');
            }
        } catch (e) {
            console.error('Parse/import error:', e);
            toast.error('Failed to import load');
        } finally {
            setProcessing(false);
        }
    };

    // Handle screenshot upload and OCR
    const handleScreenshotUpload = async (file) => {
        if (!file) return;
        
        setScreenshotFile(file);
        setProcessing(true);
        
        try {
            // Upload for OCR processing
            const formData = new FormData();
            formData.append('file', file);
            
            const res = await fetch(`${API_URL}/api/ocr/load-screenshot`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if (res.ok) {
                const data = await res.json();
                
                if (data.load_id) {
                    // Load was auto-created
                    toast.success(`Load ${data.load_id} imported from screenshot!`);
                    setLastImported({
                        load_id: data.load_id,
                        route: `${data.origin_city || 'Unknown'} → ${data.destination_city || 'Unknown'}`,
                        rate: data.rate
                    });
                    setScreenshotFile(null);
                    onLoadCreated?.();
                } else if (data.extracted_text) {
                    // Got text but need to parse
                    setPastedText(data.extracted_text);
                    setMode('paste');
                    toast.info('Text extracted. Review and click "Import Load" to create.');
                } else {
                    toast.error('Could not extract load data from screenshot');
                }
            } else {
                // Fallback - OCR endpoint may not exist yet
                toast.info('Screenshot OCR processing not available. Please paste load text instead.');
                setMode('paste');
            }
        } catch (e) {
            console.error('Screenshot upload error:', e);
            toast.info('Screenshot processing unavailable. Please paste load text.');
            setMode('paste');
        } finally {
            setProcessing(false);
        }
    };

    // Parse load text into structured data
    const parseLoadText = (text) => {
        const parsed = {};
        
        // Try to extract city/state pairs (e.g., "Chicago, IL" or "Chicago IL")
        const cityStatePattern = /([A-Za-z\s]+),?\s*([A-Z]{2})\b/g;
        const matches = [...text.matchAll(cityStatePattern)];
        
        if (matches.length >= 2) {
            parsed.origin_city = matches[0][1].trim();
            parsed.origin_state = matches[0][2].toUpperCase();
            parsed.destination_city = matches[1][1].trim();
            parsed.destination_state = matches[1][2].toUpperCase();
        } else if (matches.length === 1) {
            // Only found one city, try to find another pattern
            parsed.origin_city = matches[0][1].trim();
            parsed.origin_state = matches[0][2].toUpperCase();
        }
        
        // Try to extract rate (look for dollar amounts)
        const ratePatterns = [
            /\$\s*([\d,]+(?:\.\d{2})?)/,  // $2,500 or $2500.00
            /rate[:\s]*\$?\s*([\d,]+)/i,   // Rate: $2500 or Rate: 2500
            /pay[:\s]*\$?\s*([\d,]+)/i,    // Pay: 2500
            /([\d,]{4,})/                   // Just a 4+ digit number
        ];
        
        for (const pattern of ratePatterns) {
            const rateMatch = text.match(pattern);
            if (rateMatch) {
                parsed.rate = parseFloat(rateMatch[1].replace(/,/g, ''));
                if (parsed.rate > 100) break; // Valid rate found
            }
        }
        
        // Try to extract distance
        const distanceMatch = text.match(/(\d+)\s*(?:mi|miles?)/i);
        if (distanceMatch) {
            parsed.distance = parseInt(distanceMatch[1]);
        }
        
        // Try to extract broker name
        const brokerMatch = text.match(/(?:broker|company|shipper)[:\s]*([A-Za-z0-9\s&]+?)(?:\n|$|,|\|)/i);
        if (brokerMatch) {
            parsed.broker_name = brokerMatch[1].trim();
        }
        
        // Try to extract equipment type
        const equipmentMatch = text.match(/(?:van|reefer|flatbed|dry\s*van)/i);
        if (equipmentMatch) {
            const eq = equipmentMatch[0].toLowerCase();
            if (eq.includes('reefer')) parsed.equipment_type = 'Reefer';
            else if (eq.includes('flatbed')) parsed.equipment_type = 'Flatbed';
            else parsed.equipment_type = 'Van';
        }
        
        // Try to extract pickup date
        const dateMatch = text.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-]?(\d{2,4})?/);
        if (dateMatch) {
            const month = dateMatch[1].padStart(2, '0');
            const day = dateMatch[2].padStart(2, '0');
            const year = dateMatch[3] ? (dateMatch[3].length === 2 ? '20' + dateMatch[3] : dateMatch[3]) : new Date().getFullYear();
            parsed.pickup_date = `${year}-${month}-${day}`;
        }
        
        return parsed;
    };

    return (
        <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <FileUp className="w-4 h-4 text-cyan-400" />
                    Import Load
                </CardTitle>
                <p className="text-xs text-zinc-500 mt-1">
                    Paste load board text or upload screenshot - auto-imports to system
                </p>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Mode Toggle */}
                <div className="flex gap-2">
                    <Button
                        variant={mode === 'paste' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setMode('paste')}
                        className={mode === 'paste' ? 'bg-cyan-600' : 'border-zinc-700'}
                    >
                        <Clipboard className="w-3 h-3 mr-1" />
                        Paste Load Board
                    </Button>
                    <Button
                        variant={mode === 'screenshot' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setMode('screenshot')}
                        className={mode === 'screenshot' ? 'bg-cyan-600' : 'border-zinc-700'}
                    >
                        <Image className="w-3 h-3 mr-1" />
                        Upload Screenshot
                    </Button>
                </div>

                {mode === 'paste' ? (
                    /* Paste Mode */
                    <div className="space-y-3">
                        <Textarea
                            value={pastedText}
                            onChange={(e) => setPastedText(e.target.value)}
                            placeholder="Paste load details from load board here...

Example:
Chicago, IL to Los Angeles, CA
$2,500 - 1,800 miles
Pickup: 03/15
Broker: ABC Logistics"
                            className="bg-zinc-800 border-zinc-700 min-h-[140px] text-sm"
                            data-testid="paste-load-textarea"
                        />
                        <Button
                            onClick={handleParsePaste}
                            disabled={processing || !pastedText.trim()}
                            className="w-full bg-cyan-600 hover:bg-cyan-700"
                            data-testid="import-load-btn"
                        >
                            {processing ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <FileUp className="w-4 h-4 mr-2" />
                            )}
                            Import Load
                        </Button>
                    </div>
                ) : (
                    /* Screenshot Mode */
                    <div className="space-y-3">
                        <div 
                            className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center hover:border-cyan-500/50 transition-colors cursor-pointer"
                            onClick={() => document.getElementById('screenshot-upload-input')?.click()}
                        >
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleScreenshotUpload(e.target.files?.[0])}
                                className="hidden"
                                id="screenshot-upload-input"
                            />
                            {processing ? (
                                <div className="py-2">
                                    <Loader2 className="w-8 h-8 mx-auto text-cyan-400 animate-spin mb-2" />
                                    <p className="text-sm text-zinc-400">Processing screenshot...</p>
                                </div>
                            ) : screenshotFile ? (
                                <div className="py-2">
                                    <CheckCircle2 className="w-8 h-8 mx-auto text-green-400 mb-2" />
                                    <p className="text-sm text-zinc-300">{screenshotFile.name}</p>
                                    <p className="text-xs text-zinc-500 mt-1">Click to upload a different file</p>
                                </div>
                            ) : (
                                <div className="py-2">
                                    <Image className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
                                    <p className="text-sm text-zinc-400">Click or drag to upload screenshot</p>
                                    <p className="text-xs text-zinc-500 mt-1">Supports PNG, JPG, WEBP</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Last Imported Success */}
                {lastImported && (
                    <div className="p-3 bg-green-900/20 border border-green-700/30 rounded-lg">
                        <div className="flex items-center gap-2 text-green-400 text-sm">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>Imported: {lastImported.load_id}</span>
                        </div>
                        <p className="text-xs text-zinc-400 mt-1">
                            {lastImported.route} • {lastImported.rate ? formatCurrency(lastImported.rate) : 'Rate TBD'}
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ==================== MAIN PAGE COMPONENT ====================
export default function LoadsPage() {
    const navigate = useNavigate();
    const [loads, setLoads] = useState([]);
    const [trucks, setTrucks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAllLoadsExpanded, setIsAllLoadsExpanded] = useState(false);
    const [showResetDialog, setShowResetDialog] = useState(false);
    const [resetting, setResetting] = useState(false);

    // Fetch data
    const fetchData = useCallback(async () => {
        try {
            const [loadsRes, trucksRes] = await Promise.all([
                fetch(`${API_URL}/api/loads`, { credentials: 'include' }),
                fetch(`${API_URL}/api/trucks`, { credentials: 'include' })
            ]);

            if (loadsRes.ok) {
                const loadsData = await loadsRes.json();
                setLoads(loadsData);
            }

            if (trucksRes.ok) {
                const trucksData = await trucksRes.json();
                setTrucks(trucksData);
            }
        } catch (e) {
            console.error('Fetch error:', e);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Reset database handler - clears all test data
    const handleResetDatabase = async () => {
        setResetting(true);
        try {
            const res = await fetch(`${API_URL}/api/admin/reset-database?confirm=RESET_ALL_DATA`, {
                method: 'POST',
                credentials: 'include'
            });
            
            if (res.ok) {
                const result = await res.json();
                toast.success(`Database reset complete. ${result.details ? Object.values(result.details).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0) : 0} records deleted.`);
                setShowResetDialog(false);
                // Refresh data after reset
                await fetchData();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Reset failed');
            }
        } catch (e) {
            console.error('Reset error:', e);
            toast.error('Failed to reset database');
        } finally {
            setResetting(false);
        }
    };

    // Stage load handler - move load to staged status with truck assignment
    const handleStageLoad = async (loadId, truckId) => {
        try {
            const res = await fetch(`${API_URL}/api/loads/${loadId}/stage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ truck_id: truckId })
            });

            if (res.ok) {
                toast.success(`Load ${loadId} staged for booking`);
                await fetchData();
            } else {
                const err = await res.json();
                
                // Handle compliance block error specially
                if (err.detail?.error_type === 'compliance_block') {
                    toast.error('Safety Compliance Block', {
                        description: err.detail.message,
                        duration: 10000
                    });
                    err.detail.failures?.forEach(f => {
                        toast.warning(`${f.field}: ${f.reason}`, { duration: 8000 });
                    });
                } else if (err.detail?.error_type === 'policy_block') {
                    // Handle broker credit/policy block errors
                    toast.error('Policy Block', {
                        description: err.detail.message,
                        duration: 10000
                    });
                    if (err.detail.broker_block_details?.suggested_action) {
                        toast.warning(err.detail.broker_block_details.suggested_action, { duration: 8000 });
                    }
                } else {
                    const errorMsg = typeof err.detail === 'string' 
                        ? err.detail 
                        : err.detail?.message || 'Failed to stage load';
                    toast.error(errorMsg);
                }
            }
        } catch (e) {
            console.error('Stage error:', e);
            toast.error('Failed to stage load');
        }
    };

    // Advance load handler - progress load to next status
    // Uses the same endpoint as Truck Monitoring for consistency
    const handleAdvanceLoad = async (loadId) => {
        console.log('[AdvanceLoad] Starting advance for load:', loadId);
        const load = loads.find(l => l.load_id === loadId);
        if (!load) {
            toast.error('Load not found');
            return;
        }

        // Check if already at final status
        if (load.status === 'completed') {
            toast.info('Load is at final status (completed)');
            return;
        }

        try {
            // Use the same advance endpoint as Truck Monitoring
            // This handles the full lifecycle and auto-activates next staged load
            console.log('[AdvanceLoad] Fetching:', `${API_URL}/api/simulation/advance-status/${loadId}`);
            const res = await fetch(`${API_URL}/api/simulation/advance-status/${loadId}`, {
                method: 'POST',
                credentials: 'include'
            });

            console.log('[AdvanceLoad] Response status:', res.status);
            
            // Read body once as text, then parse
            const responseText = await res.text();
            console.log('[AdvanceLoad] Response text length:', responseText.length);
            
            let data;
            try {
                data = JSON.parse(responseText);
                console.log('[AdvanceLoad] Parsed JSON successfully');
            } catch (parseErr) {
                console.log('[AdvanceLoad] JSON parse failed:', parseErr.message);
                if (res.ok) {
                    toast.success('Load advanced');
                    await fetchData();
                } else {
                    toast.error('Failed to advance load', { description: responseText || `HTTP ${res.status}` });
                }
                return;
            }

            if (res.ok) {
                // Check if next load was auto-activated (when current load completed)
                if (data.next_load_activated) {
                    toast.success('Load completed & next load activated', {
                        description: `${data.previous_status} → ${data.new_status}. Next load: ${data.next_load_activated.origin} → ${data.next_load_activated.destination}`
                    });
                } else {
                    toast.success(`Load advanced: ${data.previous_status} → ${data.new_status}`);
                }
                
                await fetchData();
            } else {
                // Handle structured error types
                if (data.detail?.error_type) {
                    const errorType = data.detail.error_type;
                    const message = data.detail.message || 'Unknown error';
                    
                    if (errorType === 'compliance_block') {
                        toast.error('Safety Compliance Block', { description: message, duration: 10000 });
                        data.detail.failures?.forEach(f => {
                            toast.warning(`${f.field}: ${f.reason}`, { duration: 8000 });
                        });
                    } else if (errorType === 'ratecon_required') {
                        toast.error('Rate Confirmation Required', { description: message, duration: 8000 });
                    } else if (errorType === 'ratecon_unsigned') {
                        toast.error('Rate Confirmation Not Signed', { description: message, duration: 8000 });
                    } else if (errorType === 'policy_block') {
                        toast.error('Policy Block', { description: message, duration: 8000 });
                    } else {
                        toast.error('Failed to advance load', { description: message });
                    }
                } else {
                    toast.error(typeof data.detail === 'string' ? data.detail : data.detail?.message || 'Failed to advance load');
                }
            }
        } catch (e) {
            console.error('Advance error:', e);
            toast.error('Failed to advance load', { description: e.message });
        }
    };

    // Open load record handler - navigate to Load Record Document Center
    const handleOpenLoad = (loadId) => {
        navigate(`/loads/${loadId}`);
    };

    // Unassign load handler - remove truck assignment and return to Available
    const handleUnassignLoad = async (loadId, reason) => {
        try {
            const res = await fetch(`${API_URL}/api/loads/${loadId}/unassign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ reason })
            });

            if (res.ok) {
                const data = await res.json();
                toast.success('Load unassigned successfully', {
                    description: `Status changed from ${data.previous_status} to available`
                });
                await fetchData();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to unassign load');
            }
        } catch (e) {
            console.error('Unassign error:', e);
            toast.error('Failed to unassign load');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
        );
    }

    return (
        <div className={`space-y-6 p-4 md:p-6 ${isAllLoadsExpanded ? 'h-screen overflow-hidden' : ''}`}>
            {/* Header - Hidden when expanded */}
            {!isAllLoadsExpanded && (
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-white">My Loads</h1>
                        <p className="text-zinc-400 text-sm">
                            Generate, import, and manage loads
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setShowResetDialog(true)}
                            className="border-red-700 text-red-400 hover:bg-red-900/30"
                            data-testid="reset-database-btn"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Reset DB
                        </Button>
                        <Button
                            variant="outline"
                            onClick={fetchData}
                            className="border-zinc-700"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh
                        </Button>
                    </div>
                </div>
            )}

            {/* Reset Database Confirmation Dialog */}
            <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
                <DialogContent className="bg-zinc-900 border-zinc-700">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            Reset Database
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            This will permanently delete ALL test data including:
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <ul className="text-sm text-zinc-300 space-y-1">
                            <li>• All loads and load documents</li>
                            <li>• All brokers and carriers</li>
                            <li>• All operations queue items</li>
                            <li>• All invoices and accounting entries</li>
                            <li>• All driver duty states and HOS records</li>
                            <li>• All tracking and email logs</li>
                        </ul>
                        <p className="mt-4 text-amber-400 text-sm font-medium">
                            User accounts, settings, and truck profiles will be preserved.
                        </p>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setShowResetDialog(false)}
                            className="border-zinc-700"
                            disabled={resetting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleResetDatabase}
                            disabled={resetting}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-reset-btn"
                        >
                            {resetting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Resetting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Yes, Reset Database
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Two-Panel Layout - Hidden when expanded */}
            {!isAllLoadsExpanded && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Panel 1: Load Simulation */}
                    <LoadSimulationPanel 
                        trucks={trucks} 
                        onLoadGenerated={fetchData}
                    />

                    {/* Panel 2: Import Load */}
                    <ImportLoadEntryPanel 
                        onLoadCreated={fetchData}
                    />
                </div>
            )}

            {/* Panel 3: All Loads (Full Width / Full Screen when expanded) */}
            <AllLoadsPanel 
                loads={loads}
                trucks={trucks}
                onRefresh={fetchData}
                onStageLoad={handleStageLoad}
                onAdvanceLoad={handleAdvanceLoad}
                onUnassignLoad={handleUnassignLoad}
                isExpanded={isAllLoadsExpanded}
                onToggleExpand={() => setIsAllLoadsExpanded(!isAllLoadsExpanded)}
                onOpenLoad={handleOpenLoad}
            />

            {/* Backdrop when expanded (for visual effect) */}
            {isAllLoadsExpanded && (
                <div 
                    className="fixed inset-0 bg-black/80 z-40"
                    onClick={() => setIsAllLoadsExpanded(false)}
                />
            )}
        </div>
    );
}
